import { FC, useCallback, useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { debounce, get } from 'lodash';
import { TableContext } from './';
import { arcadeApiClient } from 'apiClients';
import { colors } from '../../../../../styles';
import { useUserInfo } from 'hooks';
import { NO_FILTER } from 'hooks/useTable';
import styles from './AllFiltersDrawer.module.css';
export const UserSelectFilter: FC<UserSelectFilterProps> = ({
  columnId,
  onChange,
  value
}) => {
  const {
    filterInfo
  } = useContext(TableContext);
  const thisFilter = filterInfo.find(filter => filter.columnId === columnId) as {
    columnName: string;
  };
  const resetValue = get(thisFilter, 'filterConfig.resetValue', NO_FILTER);
  const [options, setOptions] = useState<SelectionOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectionOption | null>(null);
  const [loadingInitialValue, setLoadingInitialValue] = useState(true);

  /* Incoming value will be undefined or a userId, possibly from SessionStorage
     hydration.  After mount, attempt to resolve that userId to a name for display
     in the element. If that fails, reset the value.
  */
  const idToLookup = value !== NO_FILTER ? value : null;
  const {
    error,
    fullName,
    loading
  } = useUserInfo(idToLookup);
  useEffect(() => {
    /* Don't worry about loading a value if it's not present
       or is the reset value
    */
    if (!value || value === resetValue) return setLoadingInitialValue(false);
    if (loading) return;
    if (error) {
      onChange(resetValue);
    } else {
      setSelectedOption({
        label: fullName,
        value
      });
    }
    setLoadingInitialValue(false);
  }, [error, fullName]);

  /* Clear Filters in the parent can reset this filter to its initial state.
     In that case, nullify the selected option and clear the options list
     so that the Select makes sense contextually
  */
  useEffect(() => {
    if (value === resetValue) {
      setOptions([]);
      setSelectedOption(null);
    }
  }, [value]);

  /* DO return something other than the promise for this handler (like undefined as here)
     Single line arrow function was overtaking inputValue in future
     calls for some reason *shrug* 
  */
  const getUsersByInput = useCallback((inputValue: string) => {
    arcadeApiClient.get(`/multi_platform/manage/people?rows_per_page=10&page=0&query=${inputValue}`).then(res => res.data.people.map(person => ({
      label: person.name,
      value: `${person.id}`
    }))).then(setOptions);
  }, []);
  const debouncedSearch = useCallback(debounce(getUsersByInput, 375), []);
  const handleInputChange = (inputValue: string, {
    action
  }) => {
    switch (action) {
      case InputAction.InputChange:
        return debouncedSearch(inputValue);
    }
  };
  const handleOptionSelected = (option: SelectionOption) => {
    const optionUnlessAll = option.value === NO_FILTER ? null : option;
    setSelectedOption(optionUnlessAll);
    onChange(option.value);
  };
  const optionsToUse = options.length > 0 || selectedOption !== null ? [{
    label: 'All Users',
    value: NO_FILTER
  }].concat(options) : options;
  return <div className={styles.filterComponent} data-sentry-component="UserSelectFilter" data-sentry-source-file="UserSelectFilter.tsx">
      <span className={styles.filterLabel}>{thisFilter.columnName}</span>
      <Select classNames={{
      dropdownIndicator: () => styles.dropdownIndicator
    }} isDisabled={loadingInitialValue} isLoading={loadingInitialValue} noOptionsMessage={({
      inputValue
    }) => inputValue ? 'No users found' : 'Search by name...'} onChange={handleOptionSelected} onInputChange={handleInputChange} options={optionsToUse} placeholder="Select a learner" styles={customStyles} value={selectedOption} data-sentry-element="Select" data-sentry-source-file="UserSelectFilter.tsx" />
    </div>;
};
const customStyles = {
  control: baseStyles => ({
    ...baseStyles,
    borderColor: colors.cloud[300],
    borderRadius: '99px'
  }),
  dropdownIndicator: baseStyles => ({
    ...baseStyles,
    color: colors.cloud[800],
    paddingRight: '11px'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  placeholder: baseStyles => ({
    ...baseStyles,
    color: colors.cloud[800],
    fontFamily: 'Nunito Sans',
    fontWeight: 600,
    paddingLeft: '8px'
  }),
  singleValue: baseStyles => ({
    ...baseStyles,
    color: colors.cloud[800],
    fontFamily: 'Nunito Sans',
    fontWeight: 600,
    paddingLeft: '8px'
  })
};
interface UserSelectFilterProps {
  columnId: string;
  onChange: (value: string) => void;
  value: string;
}
type SelectionOption = {
  label: string;
  value: string;
};
enum InputAction {
  InputChange = 'input-change',
  SetValue = 'set-value',
}